import React, { useState, useEffect } from 'react';
import { usersAdminService, ProfileWithEmail, UserRole } from '../../services/users.admin.service';
import { useAuth } from '../../contexts/AuthContext';

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ProfileWithEmail[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ProfileWithEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Filters
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProfileWithEmail | null>(null);

  // Form state for edit
  const [editFormData, setEditFormData] = useState({
    display_name: '',
    username: '',
    bio: '',
    avatar_url: '',
    role: 'user' as UserRole,
  });

  // Form state for points adjustment
  const [pointsAdjustment, setPointsAdjustment] = useState({
    points: 0,
    description: '',
  });

  // User stats
  const [userStats, setUserStats] = useState<any>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, roleFilter, statusFilter, searchQuery]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersAdminService.getAll();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter((u) => u.role === roleFilter);
    }

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter((u) => !u.is_suspended);
    } else if (statusFilter === 'suspended') {
      filtered = filtered.filter((u) => u.is_suspended);
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.display_name?.toLowerCase().includes(query) ||
          u.username?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query)
      );
    }

    setFilteredUsers(filtered);
  };

  const handleOpenEditModal = (user: ProfileWithEmail) => {
    setSelectedUser(user);
    setEditFormData({
      display_name: user.display_name || '',
      username: user.username || '',
      bio: user.bio || '',
      avatar_url: user.avatar_url || '',
      role: (user.role || 'user') as UserRole,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    setError('');
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    // Prevent removing own admin role
    if (selectedUser.id === currentUser?.id && editFormData.role !== 'admin') {
      setError('No puedes quitarte tu propio rol de administrador');
      return;
    }

    try {
      await usersAdminService.update(selectedUser.id, editFormData);
      await loadUsers();
      handleCloseEditModal();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar usuario');
    }
  };

  const handleToggleSuspend = async (userId: string, currentlySuspended: boolean) => {
    // Prevent self-suspension
    if (userId === currentUser?.id) {
      setError('No puedes suspender tu propia cuenta');
      return;
    }

    const action = currentlySuspended ? 'reactivar' : 'suspender';
    if (!window.confirm(`¿Estás seguro de ${action} este usuario?`)) return;

    try {
      if (currentlySuspended) {
        await usersAdminService.unsuspend(userId);
      } else {
        await usersAdminService.suspend(userId);
      }
      await loadUsers();
    } catch (err: any) {
      setError(err.message || `Error al ${action} usuario`);
    }
  };

  const handleOpenPointsModal = (user: ProfileWithEmail) => {
    setSelectedUser(user);
    setPointsAdjustment({ points: 0, description: '' });
    setIsPointsModalOpen(true);
  };

  const handleClosePointsModal = () => {
    setIsPointsModalOpen(false);
    setSelectedUser(null);
    setError('');
  };

  const handleSubmitPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (pointsAdjustment.points === 0) {
      setError('Debes especificar una cantidad de puntos');
      return;
    }

    try {
      await usersAdminService.adjustPoints(
        selectedUser.id,
        pointsAdjustment.points,
        pointsAdjustment.description
      );
      await loadUsers();
      handleClosePointsModal();
    } catch (err: any) {
      setError(err.message || 'Error al ajustar puntos');
    }
  };

  const handleOpenStatsModal = async (user: ProfileWithEmail) => {
    setSelectedUser(user);
    setIsStatsModalOpen(true);
    try {
      const stats = await usersAdminService.getStats(user.id);
      setUserStats(stats);
    } catch (err: any) {
      setError(err.message || 'Error al cargar estadísticas');
    }
  };

  const handleCloseStatsModal = () => {
    setIsStatsModalOpen(false);
    setSelectedUser(null);
    setUserStats(null);
  };

  const getRoleBadge = (role: string | null) => {
    const badges = {
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      moderator: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      user: 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-300',
    };
    const labels = {
      admin: 'Admin',
      moderator: 'Moderador',
      user: 'Usuario',
    };
    const roleKey = (role || 'user') as keyof typeof badges;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[roleKey]}`}>
        {labels[roleKey]}
      </span>
    );
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Usuarios</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gestiona los usuarios de la plataforma
          </p>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Total: {filteredUsers.length} usuarios
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">
            ×
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              placeholder="Nombre, username o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por rol
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              <option value="all">Todos los roles</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderador</option>
              <option value="user">Usuario</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="suspended">Suspendidos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4 md:space-y-0">
        {filteredUsers.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No se encontraron usuarios con los filtros aplicados.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {filteredUsers.map((user) => (
                <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="space-y-3">
                    {/* Avatar and Name */}
                    <div className="flex items-start gap-3">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name || 'Avatar'}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold">
                          {(user.display_name || user.username || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {user.display_name || 'Sin nombre'}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          @{user.username || 'sin-username'}
                        </p>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      {getRoleBadge(user.role)}
                      {user.is_suspended && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                          ❌ Suspendido
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-600 dark:text-gray-400">
                        📧 {user.email || 'Sin email'}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        ⭐ {user.total_points || 0} puntos
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        📅 Registrado: {formatDate(user.created_at)}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        📊 {user.vote_count || 0} votos • {user.post_count || 0} posts •{' '}
                        {user.comment_count || 0} comentarios
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleOpenEditModal(user)}
                        className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 text-sm font-medium"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleOpenPointsModal(user)}
                        className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 text-sm font-medium"
                      >
                        Puntos
                      </button>
                      <button
                        onClick={() => handleOpenStatsModal(user)}
                        className="text-fuchsia-600 dark:text-fuchsia-400 hover:text-fuchsia-900 dark:hover:text-fuchsia-300 text-sm font-medium"
                      >
                        Estadísticas
                      </button>
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleToggleSuspend(user.id, user.is_suspended || false)}
                          className={`text-sm font-medium ${
                            user.is_suspended
                              ? 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300'
                              : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                          }`}
                        >
                          {user.is_suspended ? 'Reactivar' : 'Suspender'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Puntos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actividad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Registro
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt={user.display_name || 'Avatar'}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold">
                              {(user.display_name || user.username || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {user.display_name || 'Sin nombre'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              @{user.username || 'sin-username'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {user.email || 'Sin email'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getRoleBadge(user.role)}
                          {user.is_suspended && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                              ❌
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {user.total_points || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {user.vote_count || 0}V • {user.post_count || 0}P • {user.comment_count || 0}C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenEditModal(user)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 mr-3"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleOpenPointsModal(user)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 mr-3"
                        >
                          Puntos
                        </button>
                        <button
                          onClick={() => handleOpenStatsModal(user)}
                          className="text-fuchsia-600 dark:text-fuchsia-400 hover:text-fuchsia-900 dark:hover:text-fuchsia-300 mr-3"
                        >
                          Stats
                        </button>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleToggleSuspend(user.id, user.is_suspended || false)}
                            className={
                              user.is_suspended
                                ? 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300'
                                : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                            }
                          >
                            {user.is_suspended ? 'Reactivar' : 'Suspender'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseEditModal}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmitEdit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Editar Usuario
                  </h3>

                  {error && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre para mostrar
                      </label>
                      <input
                        type="text"
                        value={editFormData.display_name}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, display_name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={editFormData.username}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, username: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bio
                      </label>
                      <textarea
                        value={editFormData.bio}
                        onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Avatar URL
                      </label>
                      <input
                        type="text"
                        value={editFormData.avatar_url}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, avatar_url: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Rol
                      </label>
                      <select
                        value={editFormData.role}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, role: e.target.value as UserRole })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="user">Usuario</option>
                        <option value="moderator">Moderador</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Points Adjustment Modal */}
      {isPointsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleClosePointsModal}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmitPoints}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Ajustar Puntos - {selectedUser.display_name}
                  </h3>

                  {error && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 rounded-lg p-4">
                      <p className="text-sm text-violet-800 dark:text-violet-300">
                        Puntos actuales: <strong>{selectedUser.total_points || 0}</strong>
                      </p>
                      <p className="text-sm text-violet-800 dark:text-violet-300 mt-1">
                        Nuevos puntos:{' '}
                        <strong>{(selectedUser.total_points || 0) + pointsAdjustment.points}</strong>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Puntos a ajustar (usa números negativos para restar)
                      </label>
                      <input
                        type="number"
                        required
                        value={pointsAdjustment.points}
                        onChange={(e) =>
                          setPointsAdjustment({
                            ...pointsAdjustment,
                            points: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descripción (opcional)
                      </label>
                      <textarea
                        value={pointsAdjustment.description}
                        onChange={(e) =>
                          setPointsAdjustment({ ...pointsAdjustment, description: e.target.value })
                        }
                        rows={3}
                        placeholder="Razón del ajuste..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Ajustar
                  </button>
                  <button
                    type="button"
                    onClick={handleClosePointsModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {isStatsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseStatsModal}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Estadísticas - {selectedUser.display_name}
                </h3>

                {userStats ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-violet-50 dark:bg-violet-900/30 rounded-lg">
                        <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                          {userStats.total_votes}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Votos</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {userStats.total_posts}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Posts</div>
                      </div>
                      <div className="text-center p-4 bg-fuchsia-50 dark:bg-fuchsia-900/30 rounded-lg">
                        <div className="text-2xl font-bold text-fuchsia-600 dark:text-fuchsia-400">
                          {userStats.total_comments}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Comentarios</div>
                      </div>
                    </div>

                    {userStats.points_by_reason.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                          Puntos por razón
                        </h4>
                        <div className="space-y-2">
                          {userStats.points_by_reason.map((item: any, index: number) => (
                            <div
                              key={index}
                              className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                {item.reason.replace('_', ' ')}
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {item.total_points > 0 ? '+' : ''}
                                {item.total_points}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6">
                <button
                  type="button"
                  onClick={handleCloseStatsModal}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
